import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '@/lib/api';
import { showToast } from '@/components/ui/Toast';

interface InteractionStatus {
  isLiked: boolean;
  isSaved: boolean;
}

/**
 * Custom hook for managing artwork interactions (likes and saves)
 * @param artworkId - The artwork UUID
 * @returns Hook state and methods
 */
export function useArtworkInteractions(artworkId: string) {
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch initial interaction status
  const fetchStatus = useCallback(async () => {
    if (!artworkId) return;

    try {
      const response: any = await apiFetch(`/artworks/${artworkId}/interaction-status`, {
        cache: 'no-store'
      });

      if (response?.success && response.data) {
        setIsLiked(Boolean(response.data.isLiked));
        setIsSaved(Boolean(response.data.isSaved));
      }
    } catch (error) {
      // Silently fail - user might not be logged in
      console.warn('Failed to fetch interaction status:', error);
    }
  }, [artworkId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Toggle like
  const toggleLike = useCallback(async () => {
    if (loading) return;

    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) {
      showToast('Você precisa estar logado para curtir artes', 'error');
      return;
    }

    setLoading(true);
    const previousState = isLiked;
    const nextState = !isLiked;

    try {
      // Optimistic update
      setIsLiked(nextState);

      const response: any = await apiFetch(`/artworks/${artworkId}/like`, {
        method: 'POST',
        cache: 'no-store'
      });

      if (response?.action === 'liked') {
        setIsLiked(true);
        showToast('Arte curtida! ❤️', 'success');
      } else if (response?.action === 'unliked') {
        setIsLiked(false);
        showToast('Curtida removida', 'success');
      } else {
        await fetchStatus();
      }
    } catch (error: any) {
      // Revert on error
      setIsLiked(previousState);

      if (error?.message?.includes('401') || error?.message?.includes('authentication')) {
        showToast('Você precisa estar logado para curtir artes', 'error');
      } else {
        showToast(error?.message || 'Erro ao curtir arte', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [artworkId, fetchStatus, isLiked, loading]);

  // Toggle save
  const toggleSave = useCallback(async () => {
    if (loading) return;

    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) {
      showToast('Você precisa estar logado para salvar artes', 'error');
      return;
    }

    setLoading(true);
    const previousState = isSaved;
    const nextState = !isSaved;

    try {
      // Optimistic update
      setIsSaved(nextState);

      const response: any = await apiFetch(`/artworks/${artworkId}/save`, {
        method: 'POST',
        cache: 'no-store'
      });

      if (response?.action === 'saved') {
        setIsSaved(true);
        showToast('Arte salva! 💾', 'success');
      } else if (response?.action === 'unsaved') {
        setIsSaved(false);
        showToast('Removido dos salvos', 'success');
      } else {
        await fetchStatus();
      }
    } catch (error: any) {
      // Revert on error
      setIsSaved(previousState);

      if (error?.message?.includes('401') || error?.message?.includes('authentication')) {
        showToast('Você precisa estar logado para salvar artes', 'error');
      } else {
        showToast(error?.message || 'Erro ao salvar arte', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [artworkId, fetchStatus, isSaved, loading]);

  return {
    isLiked,
    isSaved,
    loading,
    toggleLike,
    toggleSave,
    refetch: fetchStatus
  };
}

/**
 * Hook for fetching user's liked artworks
 */
export function useLikedArtworks(page: number = 1, limit: number = 20) {
  const [artworks, setArtworks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLikedArtworks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: any = await apiFetch(`/user/liked-artworks?page=${page}&limit=${limit}`);
      if (response.success) {
        setArtworks(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar artes curtidas');
      showToast('Erro ao carregar artes curtidas', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchLikedArtworks();
  }, [fetchLikedArtworks]);

  return { artworks, loading, error, refetch: fetchLikedArtworks };
}

/**
 * Hook for fetching user's saved artworks
 */
export function useSavedArtworks(page: number = 1, limit: number = 20) {
  const [artworks, setArtworks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSavedArtworks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: any = await apiFetch(`/user/saved-artworks?page=${page}&limit=${limit}`);
      if (response.success) {
        setArtworks(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar artes salvas');
      showToast('Erro ao carregar artes salvas', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchSavedArtworks();
  }, [fetchSavedArtworks]);

  return { artworks, loading, error, refetch: fetchSavedArtworks };
}
